import { Card, CardContent } from '@packages/shared/src/ui/Card/Card';
import type { EnergieEfficiencyFlag } from '../../types';
import { EnergyEfficiencyFlagItem } from '../../atoms/EnergyEfficiencyFlagItem/EnergyEfficiencyFlagItem';
import { DataSheets } from '../../atoms/DataSheets/DataSheets';

/**
 * Determines whether efficiency flags cards should be rendered.
 *
 * @param {Object} params - The parameters object
 * @param {EnergieEfficiencyFlag[]} params.efficiencyFlags - Array of efficiency flags to check
 * @returns {boolean} True if there are efficiency flags to display, false otherwise
 */
export const checkRenderEfficiencyFlagsCards = (
  efficiencyFlags: EnergyEfficiencyFlagsCardProps['efficiencyFlags'],
): efficiencyFlags is NonNullable<EnergyEfficiencyFlagsCardProps['efficiencyFlags']> =>
  efficiencyFlags && efficiencyFlags.length > 0;

/**
 * Props for the efficiency flags card.
 *
 * `efficiencyFlags` contains the matched energy efficiency entries that are rendered as flag items.
 * `unmatchedDataSheets` can be used to render extra datasheet downloads below the flags.
 */
export type EnergyEfficiencyFlagsCardProps = {
  efficiencyFlags: EnergieEfficiencyFlag[];
  unmatchedDataSheets?: string[];
  /** Optional product name, named in the datasheet links for screen readers. */
  productName?: string;
};

/**
 * Renders the efficiency flags card with the matched flags and any unmatched datasheet links.
 *
 * Use `checkRenderEfficiencyFlagsCards` to check if any efficiency data is available before rendering this component.
 *
 * @example
 * ```tsx
 * checkRenderEfficiencyFlagsCards({ efficiencyFlags }) && (
 *   <EnergyEfficiencyFlagsCard
 *     efficiencyFlags={efficiencyFlags}
 *     unmatchedDataSheets={unmatchedDataSheets}
 *   />
 * )
 * ```
 */
export const EnergyEfficiencyFlagsCard = ({
  efficiencyFlags,
  unmatchedDataSheets,
  productName,
}: EnergyEfficiencyFlagsCardProps) => (
  <Card layout="outlined" hasAutoSeparation>
    <CardContent className="pb-2">
      <div className="grid grid-cols-[auto_1fr] items-center gap-1.5">
        {efficiencyFlags.map((flag) => (
          <EnergyEfficiencyFlagItem
            // this key should be unique enough to avoid duplicate keys when multiple flags with the same level exist, e.g. for kitchen units with multiple appliances
            key={flag.level + flag.linkToEnergieEfficiencyLabel + flag.flagDescription}
            {...flag}
            linkToEnergieEfficiencyLabel={flag.linkToEnergieEfficiencyLabel}
            productName={productName}
            // Only show the flag description if there are multiple flags -> see https://app.zeplin.io/project/5b1e541757a0c54542c6cca6/screen/6171365cd52a63a0b3c9f294
            flagDescription={efficiencyFlags.length > 1 ? flag.flagDescription : undefined}
            size="byBreakpoint"
          />
        ))}
      </div>
    </CardContent>

    {unmatchedDataSheets && unmatchedDataSheets.length > 0 && (
      <CardContent className="pb-2">
        <DataSheets dataSheetUrls={unmatchedDataSheets} productName={productName} />
      </CardContent>
    )}
  </Card>
);
